import { DelegateProvider } from '../../shared/interfaces/delegate-provider.interface';
import { TrackEvent } from '../../shared/models/tracking-event.model';
import { FunctionPropertyNames } from '../../shared/types';

// -----------------------------------------------------------------------------
export interface IframeCommunicationDelegate {
  onClosePromotionRequested?();
  onSendTrackingRequested?(event: TrackEvent);
  onRecommendationInView?();
  onOpenExternalUrl?(url: string);
  onToggleTimeoutTimerRequested?(event: any);
  onPromotionOfferAccepted?();
  onOpenNotificationSettingsRequested?();
  onTurnOffPromotionsRequested?();
}

const kMessageToDelegate = new Map<
  string,
  FunctionPropertyNames<IframeCommunicationDelegate>
>([
  ['ClosePromotion', 'onClosePromotionRequested'],
  ['SendTracking', 'onSendTrackingRequested'],
  ['RecommendationInView', 'onRecommendationInView'],
  ['OpenExternalUrl', 'onOpenExternalUrl'],
  ['ToggleTimeoutTimer', 'onToggleTimeoutTimerRequested'],
  ['PromotionOfferAccepted', 'onPromotionOfferAccepted'],
  ['OpenNotificationSettings', 'onOpenNotificationSettingsRequested'],
  ['TurnOffPromotions', 'onTurnOffPromotionsRequested'],
]);

// -----------------------------------------------------------------------------
export class IframeCommunicationServiceImpl
  implements DelegateProvider<IframeCommunicationDelegate>
{
  private readonly listeners: Set<IframeCommunicationDelegate>;
  // ---------------------------------------------------------------------------
  public constructor() {
    this.listeners = new Set<IframeCommunicationDelegate>();
    this.registerPostMessageEvents();
  }

  // ---------------------------------------------------------------------------
  public triggerListenerEvent(
    eventName: FunctionPropertyNames<IframeCommunicationDelegate>,
    param: any,
  ) {
    if (!eventName) {
      return;
    }

    for (const listener of this.listeners) {
      if (!listener[eventName]) {
        continue;
      }

      listener[eventName](param);
    }
  }

  // ---------------------------------------------------------------------------
  // DelegateProvider
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  addListener(listener: IframeCommunicationDelegate) {
    this.listeners.add(listener);
  }

  // ---------------------------------------------------------------------------
  removeListener(listener: IframeCommunicationDelegate) {
    this.listeners.delete(listener);
  }

  // ---------------------------------------------------------------------------
  private registerPostMessageEvents() {
    window.addEventListener('message', this.handleMessageEvent.bind(this));
  }

  // ---------------------------------------------------------------------------
  private handleMessageEvent(event: MessageEvent) {
    const [eventName, eventParam] = this.getMessageInfo(event);

    if (!eventName) {
      return;
    }

    this.triggerListenerEvent(eventName, eventParam);
  }

  // ---------------------------------------------------------------------------
  private getMessageInfo(
    event: MessageEvent,
  ): [FunctionPropertyNames<IframeCommunicationDelegate>, object] {
    const { data } = event;

    if (!kMessageToDelegate.has(data.action)) {
      return null;
    }

    const param = kMessageToDelegate.get(data.action);
    return [param, data.data];
  }
}
